import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LessonQuiz } from "./LessonQuiz";
import { cn } from "@/lib/utils";
import { DollarSign, PiggyBank, Calculator, Coins, CreditCard, Wallet, TrendingUp, Target, BarChart } from "lucide-react";

// Define interfaces for content structure
interface ContentSection {
  title: string;
  text: string;
}

interface KnowledgeCheck {
  type: string;
  question: string;
  options: string[];
  correct_answer: number;
  explanation: string;
}

interface LessonContentProps {
  sections: ContentSection[];
  currentSection: number;
  knowledgeChecks: KnowledgeCheck[];
  onQuizComplete?: (passed: boolean) => void;
}

/**
 * Component that renders the current section of lesson content
 * Shows quiz/knowledge checks only at the end after all content sections
 */
export const LessonContent = ({ sections, currentSection, knowledgeChecks, onQuizComplete }: LessonContentProps) => {
  // Debug logging
  console.log("Rendering lesson content with:", { 
    sectionsReceived: sections, 
    currentSection, 
    knowledgeChecksReceived: knowledgeChecks,
    sectionsLength: sections?.length || 0,
    knowledgeChecksLength: knowledgeChecks?.length || 0
  });
  
  // Create a comprehensive mapping of lesson titles to appropriate icons
  // This helps visually differentiate different lesson types
  const sectionTitleToIcon: Record<string, any> = {
    // Finance fundamentals icons
    "What is Money?": Coins,
    "How Banks Work": CreditCard,
    "Digital Money": Wallet,
    "Understanding Currency": Coins,
    "Banking Basics": CreditCard,
    "Financial Institutions": CreditCard,
    
    // Investment fundamentals icons
    "Why Invest?": TrendingUp,
    "Investment Goals": Target,
    "Risk vs Return": BarChart,
    "Investment Vehicles": TrendingUp,
    "Market Basics": BarChart,
    "Portfolio Theory": Target,
    "What is Investing?": TrendingUp,
    "The Power of Compound Growth": Target,
    "Beating Inflation": BarChart,
    "Building Long-term Wealth": TrendingUp,
    "Understanding Investment Risk": BarChart,
    "The Risk-Return Relationship": Target,
    "Types of Investment Risk": BarChart,
    "Managing Risk Through Diversification": Target,
    "Stocks: Owning Pieces of Companies": TrendingUp,
    "Bonds: Lending Money for Fixed Returns": CreditCard,
    "Mutual Funds: Professional Management": Target,
    "ETFs: Flexible and Low-Cost Options": BarChart,
    "Real Estate and REITs": Wallet,
    "Index Funds: Simple Market Participation": TrendingUp,
    
    // Investment Goals specific section icons
    "Setting SMART Investment Goals": Target,
    "Short-term vs Long-term Goals": Target,
    "Common Investment Objectives": Target,
    "Aligning Goals with Investment Strategy": Target,
    
    // Default fallback icons
    "Budgeting": DollarSign,
    "Saving": PiggyBank,
    "Financial Planning": Calculator
  };
  
  // Ensure sections is an array
  const validSections = Array.isArray(sections) && sections.length > 0 
    ? sections 
    : [];
  
  // Ensure knowledgeChecks is an array
  const validKnowledgeChecks = Array.isArray(knowledgeChecks) && knowledgeChecks.length > 0
    ? knowledgeChecks
    : [];
  
  // Check if we're past all content sections and should show the quiz
  const isQuizSection = currentSection >= validSections.length && validKnowledgeChecks.length > 0;

  // If we're at the quiz section, show all knowledge checks
  if (isQuizSection) {
    console.log("Showing knowledge checks at end of lesson:", validKnowledgeChecks);
    
    return (
      <div className="space-y-8 max-w-4xl mx-auto animate-fade-in">
        <Card className="bg-gradient-to-br from-accent-50 to-accent-100/50">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-primary">
              Knowledge Check
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-6">
              Answer all questions correctly to complete this lesson!
            </p>
            <LessonQuiz 
              questions={validKnowledgeChecks} 
              onQuizComplete={onQuizComplete}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get current section to display
  const currentSectionData = validSections[currentSection];
  
  // If no valid section data, show empty state
  if (!currentSectionData) {
    console.warn("No section data found for index:", currentSection);
    return (
      <div className="p-8 text-center text-gray-500">
        <p>No content available for this section.</p>
      </div>
    );
  }

  // Get icon for current section
  const getIconForSection = (title: string, index: number) => {
    if (sectionTitleToIcon[title]) {
      return sectionTitleToIcon[title];
    }
    
    const defaultIcons = [DollarSign, Coins, Wallet, CreditCard, PiggyBank, Calculator, TrendingUp, Target, BarChart];
    return defaultIcons[index % defaultIcons.length];
  };

  const IconComponent = getIconForSection(currentSectionData.title, currentSection);
  
  console.log("Rendering section:", currentSectionData);
  
  // Render the current section content
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <Card 
        className={cn(
          "transform transition-all duration-300 hover:scale-[1.02]",
          "bg-gradient-to-br from-white to-gray-50",
          "animate-fade-in shadow-md"
        )}
      >
        <CardHeader className="flex flex-row items-start space-y-0 space-x-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <IconComponent className="w-6 h-6 text-primary" />
          </div>
          <div>
            <CardTitle className="text-xl font-bold text-primary">
              {currentSectionData.title}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {currentSectionData.text.split('. ').map((sentence, idx) => (
              sentence && (
                <div 
                  key={idx} 
                  className="flex items-start space-x-3 p-3 rounded-lg bg-background shadow-sm"
                >
                  <span className="text-primary font-medium">•</span>
                  <p className="text-gray-700 leading-relaxed">
                    {sentence.trim() + (sentence.endsWith('.') ? '' : '.')}
                  </p>
                </div>
              )
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
